/**
 * SA-469 (spec D8, §3): how a record was born, in words — the ONE place the
 * step drawer's "How the enquiry arrived" line, the timeline's step-1 row,
 * the Sources list's kind words and the strip's no-conversation word come
 * from. The birth is the ledger's `via` on the entry step's completion when
 * present, else the record's source (rows before SA-469 carry no `via`).
 * Pure; every read of the snapshot goes through `intakeSnapshot`.
 */
import type { IntakeSource, Order, RecordSource } from '@/types/commerce'
import { whenText } from '@/components/process/stepLiveText'
import { intakeSnapshot } from './intakeSnapshot'

export type BirthKind = 'email' | 'form' | 'conversation' | 'group'

/** The ledger codes (`core.INTAKE_VIAS`). A Map, never `in` on an object — `'toString' in {}` is true. */
const VIA_BIRTH = new Map<string, BirthKind>([
  ['intake_email', 'email'], ['intake_form', 'form'], ['intake_conversation', 'conversation'],
  // SA-946: a record a WhatsApp group message was triaged into.
  ['intake_whatsapp_group', 'group'],
])

/** The ledger's `via` when it names a birth, else the record's source kind; null while neither is known. */
export function birthKind(via: unknown, sourceKind: RecordSource['kind'] | null | undefined): BirthKind | null {
  if (typeof via === 'string' && VIA_BIRTH.has(via)) return VIA_BIRTH.get(via)!
  if (sourceKind === 'email') return 'email'
  if (sourceKind === 'whatsapp-group') return 'group'
  if (sourceKind === 'conversation') return 'conversation'
  if (sourceKind === 'none') return 'form'
  return null
}

const BIRTH_WORD: Record<BirthKind, string> = {
  email: 'email', form: 'form', conversation: 'conversation', group: 'group message',
}

/** The timeline's one word for a birth ("Enquiry recorded · email · …"). */
export function birthWord(kind: BirthKind): string {
  return BIRTH_WORD[kind]
}

type Extraction = 'ok' | 'unavailable' | 'failed'
const EXTRACTION = new Set<string>(['ok', 'unavailable', 'failed'])

/** What the page already holds about the birth: the order's snapshot and `useRecordSource`. */
export interface BirthFacts {
  sourceKind: RecordSource['kind'] | null
  fromAddr: string | null
  receivedAt: string | null
  /** SA-946: on a group birth, who posted the message and in which group. */
  fromName?: string | null
  groupSubject?: string | null
  /** SA-717: who approved the triage decision the record was born from. */
  approvedBy: string | null
  customerName: string | null
  /** The source the record was born with (SA-476, `birthSource`), or null when it was born with none. */
  birthSource: { kind: 'email' | 'paste' | 'whatsapp-group'; pastedBy: string | null; fieldsSet: number; extraction: Extraction | null } | null
  extracted: number
  entered: number
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

/**
 * SA-476 (spec D1): the source the record was born with. Every record-first
 * birth writes `born_with_source`, and a later paste only appends, so the
 * birth source — when the birth carried one — is `sources[0]`. `false` says
 * it was born with none (a hand-typed form, whatever was pasted since); an
 * absent key is a conversation birth or a record born before SA-476, read
 * as `sources[0]` exactly as before.
 */
function birthSource(order: Pick<Order, 'customDetails'>): BirthFacts['birthSource'] {
  const snap = intakeSnapshot(order)
  if (snap?.bornWithSource === false) return null
  const raw = snap?.sources?.[0]
  if (!isObject(raw) || (raw.kind !== 'email' && raw.kind !== 'paste' && raw.kind !== 'whatsapp-group')) return null
  const status = isObject(raw.extraction) && typeof raw.extraction.status === 'string' && EXTRACTION.has(raw.extraction.status)
    ? (raw.extraction.status as Extraction)
    : null
  return {
    kind: raw.kind,
    pastedBy: typeof raw.pasted_by === 'string' ? raw.pasted_by : null,
    fieldsSet: Array.isArray(raw.fields_set) ? raw.fields_set.length : 0,
    extraction: status,
  }
}

export function birthFacts(order: Pick<Order, 'customDetails' | 'customerName'>, source: RecordSource | null | undefined): BirthFacts {
  const provenance = Object.values(intakeSnapshot(order)?.provenance ?? {})
  return {
    sourceKind: source?.kind ?? null,
    fromAddr: source?.email?.fromAddr ?? null,
    receivedAt: source?.email?.receivedAt ?? source?.group?.receivedAt ?? null,
    fromName: source?.group?.fromName ?? null,
    groupSubject: source?.group?.subject ?? null,
    approvedBy: source?.email?.approvedByName ?? source?.group?.approvedByName ?? null,
    customerName: order.customerName ?? null,
    birthSource: birthSource(order),
    extracted: provenance.filter((p) => p === 'extracted').length,
    entered: provenance.filter((p) => p === 'entered').length,
  }
}

const count = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`

/** The drawer's "Arrived" line (spec §3). `names` resolves `pasted_by`; an id is never shown (SA-330). */
export function birthLine(kind: BirthKind, facts: BirthFacts, names: Record<string, string> = {}, now?: Date): string {
  switch (kind) {
    case 'email': {
      const status = facts.birthSource?.kind === 'email' ? facts.birthSource.extraction : null
      return [
        `Email from ${facts.fromAddr ?? 'the sender'}`,
        facts.approvedBy ? `approved by ${facts.approvedBy}` : null,
        facts.receivedAt ? `received ${whenText(facts.receivedAt, now)}` : null,
        status && status !== 'ok'
          ? `extraction ${status}`
          : facts.birthSource?.kind === 'email' ? `${count(facts.birthSource.fieldsSet, 'field')} extracted` : null,
      ].filter(Boolean).join(' · ')
    }
    case 'group': {
      const status = facts.birthSource?.kind === 'whatsapp-group' ? facts.birthSource.extraction : null
      return [
        `Message from ${facts.fromName ?? 'a member'} ${facts.groupSubject ? `in the ${facts.groupSubject} group` : 'of a WhatsApp group'}`,
        facts.approvedBy ? `approved by ${facts.approvedBy}` : null,
        facts.receivedAt ? `received ${whenText(facts.receivedAt, now)}` : null,
        status && status !== 'ok'
          ? `extraction ${status}`
          : facts.birthSource?.kind === 'whatsapp-group' ? `${count(facts.birthSource.fieldsSet, 'field')} extracted` : null,
      ].filter(Boolean).join(' · ')
    }
    case 'form': {
      if (facts.birthSource?.kind === 'paste') {
        const name = facts.birthSource.pastedBy ? names[facts.birthSource.pastedBy] : undefined
        return `${name ? `Pasted by ${name}` : 'Pasted text'} · ${facts.extracted} extracted, ${facts.entered} entered`
      }
      return `Entered by hand · ${count(facts.extracted + facts.entered, 'field')}`
    }
    case 'conversation':
      return `Conversation with ${facts.customerName ?? 'the customer'}`
  }
}

/** The Sources list's word for a source kind. */
export function sourceKindWord(kind: IntakeSource['kind']): string {
  if (kind === 'email') return 'Original email'
  if (kind === 'whatsapp-group') return 'Group message'
  return 'Pasted text'
}

/** A source that is NOT the birth (SA-1029): an applied customer update's
 * message is the customer's later word, never the "Original email". */
export function laterSourceKindWord(kind: IntakeSource['kind']): string {
  if (kind === 'email') return 'Customer email'
  if (kind === 'whatsapp-group') return 'Group message'
  return 'Pasted text'
}

/** Which of an intake read's sources is the birth: `sources[0]` unless the
 * record says it was born with none (`birthSource`'s rule, SA-476). */
export function birthSourceIndex(bornWithSource: boolean | null | undefined): number {
  return bornWithSource === false ? -1 : 0
}

/** The strip's word for a record born without a conversation (spec §3). */
export function noConversationWord(order: Pick<Order, 'customDetails'>): string {
  return birthSource(order)?.kind === 'paste' ? 'Pasted enquiry' : 'Entered by hand'
}
